import { create } from "zustand";
import type {
  LiveChannel,
  LivePhase,
  RtmChatMessage,
} from "~/features/live/types";

export const MAX_MESSAGES = 100;

type LiveState = {
  phase: LivePhase;
  currentChannel: LiveChannel | null;
  remoteUid: number | null;
  /** リモート映像トラックが（再）公開されるたびに増える。同じ uid でもトラック更新を検知するために使う */
  remoteVideoTrackVersion: number;
  messages: RtmChatMessage[];
  currentPoint: number | null;
  durationSec: number;
  error: string | null;
  videoReady: boolean;
  /** リモート映像が現在 VideoStateFrozen（フリーズ）状態か */
  videoFrozen: boolean;
  /** RTM で受信した完全コントロール強度情報 */
  lovenseLiveUpdate: {
    intensity: number;
    duration: number;
    updatedAt: number;
  } | null;
};

type LiveActions = {
  setPhase: (phase: LivePhase) => void;
  setCurrentChannel: (channel: LiveChannel | null) => void;
  setRemoteUid: (uid: number | null) => void;
  bumpRemoteVideoTrackVersion: () => void;
  addMessage: (message: RtmChatMessage) => void;
  setCurrentPoint: (point: number | null) => void;
  incrementDuration: () => void;
  setError: (error: string | null) => void;
  setVideoReady: (ready: boolean) => void;
  setVideoFrozen: (frozen: boolean) => void;
  setLovenseLiveUpdate: (update: {
    intensity: number;
    duration: number;
    updatedAt: number;
  }) => void;
  reset: () => void;
};

const initialState: LiveState = {
  phase: "idle",
  currentChannel: null,
  remoteUid: null,
  remoteVideoTrackVersion: 0,
  messages: [],
  currentPoint: null,
  durationSec: 0,
  error: null,
  videoReady: false,
  videoFrozen: false,
  lovenseLiveUpdate: null,
};

export const useLiveStore = create<LiveState & LiveActions>()((set) => ({
  ...initialState,
  setPhase: (phase) => set({ phase }),
  setCurrentChannel: (channel) => set({ currentChannel: channel }),
  setRemoteUid: (uid) => set({ remoteUid: uid }),
  bumpRemoteVideoTrackVersion: () =>
    set((state) => ({
      remoteVideoTrackVersion: state.remoteVideoTrackVersion + 1,
    })),
  addMessage: (message) =>
    set((state) => {
      const next = [...state.messages, message];
      return {
        messages: next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next,
      };
    }),
  setCurrentPoint: (point) => set({ currentPoint: point }),
  incrementDuration: () =>
    set((state) => ({ durationSec: state.durationSec + 1 })),
  setError: (error) => set({ error }),
  setVideoReady: (ready) => set({ videoReady: ready }),
  setVideoFrozen: (frozen) => set({ videoFrozen: frozen }),
  setLovenseLiveUpdate: (update) => set({ lovenseLiveUpdate: update }),
  reset: () => set(initialState),
}));
